// ===============================================================
// 💎 PremiumCenterPage.js — Premium Center (VPN Focus UI) — FINAL
// LIVORA PLAN: i18n only + NO dir logic here (dir via html only)
// VPN PLAN: server-only purchase via Cloud Functions -> users.premium
// PURCHASE PLAN: UI never writes balances; server charges only
// path: src/components/PremiumCenterPage.js
// ===============================================================

import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { auth, db } from "../firebase";
import { doc, onSnapshot } from "firebase/firestore";

import { getFunctions, httpsCallable } from "firebase/functions";

import "./PremiumCenterPage.css";
import goldCoin from "../assets/gold.png";
import { formatLocalizedDateTime } from "../utils/localizedDate";

// ✅ ORDER: 7, 30, 60
const VPN_PACKS = [
  { key: "p7", planId: "vpn_7d", days: 7, cost: 100, badge: "" },
  { key: "p30", planId: "vpn_30d", days: 30, cost: 499, badge: "" },
  { key: "p60", planId: "vpn_60d", days: 60, cost: 799, badge: "premium.best" },
];

// language is passed in explicitly by the caller (i18n.resolvedLanguage ||
// i18n.language) rather than importing the live i18n singleton here - this
// helper lives outside the component, so it stays side-effect-free and
// trivially testable. formatLocalizedDateTime's defaults (year, month
// short, day, hour 2-digit, minute 2-digit) already match this page's
// previous formatDateTimeEn options exactly, so no custom options object
// is passed.
function fmtDate(ms, language) {
  return formatLocalizedDateTime(ms, language);
}

function isPremiumActive(userDoc) {
  const prem = userDoc?.premium || {};
  const expMs = prem?.expiresAt?.toMillis ? prem.expiresAt.toMillis() : 0;
  return !!prem?.active && expMs > Date.now();
}

export default function PremiumCenterPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const activeLanguage = i18n.resolvedLanguage || i18n.language;

  const functions = getFunctions(undefined, "us-central1");
  const buyVpnWithTokensFn = useMemo(
    () => httpsCallable(functions, "buyVpnWithTokens"),
    [functions]
  );

  // ✅ default selected
  const [selectedKey, setSelectedKey] = useState("p30");
  const [loading, setLoading] = useState(false);

  // ✅ Read-only user doc (realtime) — to show status
  const [meUserDoc, setMeUserDoc] = useState(null);

  useEffect(() => {
    const uid = auth.currentUser?.uid;
    if (!uid) {
      setMeUserDoc(null);
      return;
    }

    const unsub = onSnapshot(
      doc(db, "users", uid),
      (snap) => setMeUserDoc(snap.exists() ? snap.data() : null),
      (err) => {
        console.error("PremiumCenter userDoc snapshot error:", err);
        setMeUserDoc(null);
      }
    );

    return () => unsub();
  }, []);

  const selectedPack = useMemo(
    () => VPN_PACKS.find((p) => p.key === selectedKey) || VPN_PACKS[0],
    [selectedKey]
  );

  const prem = meUserDoc?.premium || {};
  const expMs = prem?.expiresAt?.toMillis ? prem.expiresAt.toMillis() : 0;
  const activeNow = isPremiumActive(meUserDoc);

  const handleActivate = async () => {
    if (!auth.currentUser) {
      alert(t("premium.loginRequired", { defaultValue: "يجب تسجيل الدخول أولاً" }));
      return;
    }

    // ✅ prevents accidental double purchase while already active
    // (إذا بدك السماح بالتمديد حتى لو فعال: احذف هذا الشرط)
    if (activeNow) {
      alert(
        t("premium.alreadyActive", {
          defaultValue: "VPN مفعّل بالفعل. يمكنك تغيير الدولة من الإعدادات.",
        })
      );
      return;
    }

    const planId = selectedPack?.planId;
    if (!planId) {
      alert(
        t("premium.vpn.planMissing", {
          defaultValue:
            "Plan ID is missing in the interface. Check VPN packs and selected plan.",
        })
      );
      return;
    }

    try {
      setLoading(true);

      // ✅ server decides cost/days (no trust in UI)
      await buyVpnWithTokensFn({ planId });

      alert(t("premium.vpn.activated", { defaultValue: "✅ تم تفعيل VPN بنجاح" }));
    } catch (e) {
      console.error("buyVpnWithTokens error:", e);
      alert(
        t("premium.vpn.activationFailed", {
          defaultValue: "تعذر تفعيل VPN. تأكد من الرصيد وحاول مرة أخرى",
        })
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="prem-root">
      {/* ======= Header (glass) ======= */}
      <header className="prem-top">
        <button
          type="button"
          className="prem-icon-btn"
          onClick={() => navigate(-1)}
          aria-label={t("back", { defaultValue: "رجوع" })}
        >
          ←
        </button>

        <h3 className="prem-title">
          {t("premium.centerTitle", { defaultValue: "مركز البريميوم" })}
        </h3>

        <button
          type="button"
          className="prem-icon-btn"
          onClick={() => navigate(-1)}
          aria-label={t("close", { defaultValue: "إغلاق" })}
        >
          ×
        </button>
      </header>

      {/* ======= Hero ======= */}
      <section className="prem-hero">
        <div className="prem-shield" aria-hidden="true">
          <div className="prem-shield-ring" />
          <div className="prem-shield-inner">
            <div className="prem-wifi">⌁⌁⌁</div>
            <div className="prem-vpn-text">
              {t("premium.vpn.acronym", { defaultValue: "VPN" })}
            </div>
          </div>
        </div>

        <div className="prem-hero-text">
          <div className="prem-hero-head">
            {t("premium.vpn.privacyTitle", {
              defaultValue: "VPN لخصوصية المميزة",
            })}
          </div>
          <div className="prem-hero-sub">
            {t("premium.vpn.privacySubtitle", {
              defaultValue: "تحكم كامل بهويتك داخل تختارها",
            })}
          </div>

          {/* ✅ status line (read-only) */}
          <div style={{ marginTop: 10, opacity: 0.9, fontSize: 13 }}>
            {activeNow ? (
              <>
                ✅ {t("premium.active", { defaultValue: "مشترك" })} —{" "}
                {t("premium.expiresAt", { defaultValue: "ينتهي في:" })}{" "}
                {fmtDate(expMs, activeLanguage)}
              </>
            ) : (
              <>⚠️ {t("premium.inactive", { defaultValue: "غير مشترك" })}</>
            )}
          </div>
        </div>
      </section>

      {/* ======= Features (UPDATED: SHOW instead of HIDE) ======= */}
      <section className="prem-features">
        <button
          type="button"
          className="prem-feature"
          onClick={() => navigate("/premium/vpn")}
        >
          <span className="prem-check">✓</span>
          <div className="prem-feature-text">
            <div className="prem-feature-title">
              {t("premium.vpn.changeCountry", { defaultValue: "تغيير الدولة" })}
            </div>
            <div className="prem-feature-sub">
              {t("premium.vpn.changeCountrySubtitle", {
                defaultValue: "تظهر بهوية دولة تختارها داخل التطبيق",
              })}
            </div>
          </div>
          <span className="prem-feature-icon" aria-hidden="true">
            🌐
          </span>
        </button>

        <button type="button" className="prem-feature" onClick={() => {}}>
          <span className="prem-check">✓</span>
          <div className="prem-feature-text">
            <div className="prem-feature-title">
              {t("premium.features.showReadReceipts", { defaultValue: "إظهار القراءة" })}
              <span className="prem-ticks" aria-hidden="true">
                ✓✓
              </span>
            </div>
            <div className="prem-feature-sub">
              {t("premium.features.showReadReceiptsSubtitle", {
                defaultValue: "إظهار ✓✓ القراءة باللون الأخضر عند قراءة الرسالة",
              })}
            </div>
          </div>
          <span className="prem-feature-icon" aria-hidden="true">
            ✅
          </span>
        </button>

        <button type="button" className="prem-feature" onClick={() => {}}>
          <span className="prem-check">✓</span>
          <div className="prem-feature-text">
            <div className="prem-feature-title">
              {t("premium.features.showLastSeen", { defaultValue: "إظهار آخر ظهور" })}
            </div>
            <div className="prem-feature-sub">
              {t("premium.features.showLastSeenSubtitle", {
                defaultValue: 'إظهار "متصل الآن" أو "آخر ظهور" داخل المحادثات',
              })}
            </div>
          </div>
          <span className="prem-feature-icon" aria-hidden="true">
            🟢
          </span>
        </button>
      </section>

{/* ======= Plans ======= */}
<section className="prem-plans">
  {VPN_PACKS.map((p) => {
    const active = p.key === selectedKey;
    return (
      <button
        key={p.key}
        type="button"
        className={`prem-plan ${active ? "active" : ""}`}
        onClick={() => setSelectedKey(p.key)}
      >
        <div className="prem-plan-top">
          {p.badge ? (
            <span className="prem-badge">
              {t(p.badge, { defaultValue: "المفضّل" })} 🔥
            </span>
          ) : (
            <span className="prem-badge-spacer" />
          )}
        </div>

        <div className="prem-plan-days">
          {p.days} {t("premium.days", { defaultValue: "يوم" })}
        </div>

        {/* ✅ السعر بالكوين فقط */}
        <div className="prem-plan-cost">
          <span className="prem-cost-num">{p.cost}</span>
          <img
            className="prem-coin-img"
            src={goldCoin}
            alt={t("coins.alt", { defaultValue: "Coins" })}
          />
        </div>
      </button>
    );
  })}
</section>

      {/* ======= CTA ======= */}
      <section className="prem-cta">
        <button
          type="button"
          className="prem-cta-btn"
          onClick={handleActivate}
          disabled={loading}
          title={
            activeNow
              ? t("premium.alreadyActive", { defaultValue: "VPN مفعّل بالفعل" })
              : ""
          }
        >
          <span className="prem-lock" aria-hidden="true">
            🔒
          </span>
          {loading
            ? t("premium.processing", { defaultValue: "جارٍ المعالجة..." })
            : activeNow
              ? t("premium.alreadyActiveButton", { defaultValue: "VPN مفعّل" })
              : t("premium.activateVpnNow", { defaultValue: "تفعيل VPN الآن" })}
        </button>
      </section>
    </div>
  );
}
